import React, { PropsWithChildren, useRef } from 'react';
import styled from 'styled-components';
import { useTestId } from '../../hooks/useTestId/useTestId';
import { IModalProps, useModalWrapperPositionCss } from '../../patterns/Modal/Modal';
import ReactModal, { BaseModalProps } from 'react-overlays/Modal';
import useRootClose from 'react-overlays/useRootClose';
import { DDMENU_SHADOW_SIZE } from '../../patterns/Menu/MenuBase';
import { Overlay } from '../Overlay/Overlay';
import { IShadowProps } from '../../fundamentals/shadows/shadows';
import { SheetBody } from './SheetBody';
import { ConfigurationOptions as SwipeableConfigurationOptions } from 'react-swipeable/src/types';
import { IColoursCollection } from '../../fundamentals/colours/colourInterfaces';

export const sheetColours = ({ palette }: IColoursCollection) => ({
  sheetColours: {
    handle: {
      background: palette.neutral50 // Note: ask Pierre if sheet should support variants
    }
  }
});

export interface ISheetBaseProps {
  title?: string;
  showBackButton?: boolean;
  onBackButtonClick?: React.MouseEventHandler;
  closable?: boolean;
  showHandle?: boolean;
  peek?: boolean;
  swipeableConfig?: SwipeableConfigurationOptions;
}

export interface ISheetProps extends Omit<BaseModalProps, 'children'>, Pick<IModalProps, 'blur'>, IShadowProps, ISheetBaseProps {}

const SheetContainer = styled(ReactModal)<ISheetProps>`
  ${useModalWrapperPositionCss};

  bottom: 0;
  right: 0;
  left: 0;
`;

const NoBackdrop: React.FC<PropsWithChildren<Pick<ISheetProps, 'show' | 'onHide' | 'shadow' | 'onBackButtonClick'>>> = ({
  show,
  shadow = DDMENU_SHADOW_SIZE,
  onHide,
  children,
  ...rest
}) => {
  const ref = useRef(null);
  const handleRootClose = () => {
    onHide?.();
  };

  useRootClose(ref as any, handleRootClose, {
    disabled: !show
  });

  return (
    <SheetBody shadow={shadow} ref={ref} {...rest}>
      {children}
    </SheetBody>
  );
};

export const Sheet: React.FC<PropsWithChildren<ISheetProps>> = ({
  children,
  closable,
  backdrop = true,
  blur = false,
  showBackButton,
  onBackButtonClick,
  onHide,
  showHandle,
  peek,
  swipeableConfig,
  ...rest
}) => {
  const { generateTestId } = useTestId(rest, 'Sheet');

  const handleBackButtonClick: React.MouseEventHandler = (e) => (onBackButtonClick ?? onHide)?.(e);

  const bodyProps = {
    closable,
    showBackButton,
    onHide,
    showHandle,
    peek,
    swipeableConfig,
    onBackButtonClick: handleBackButtonClick,
    ...rest
  };

  return (
    <SheetContainer backdrop={backdrop} renderBackdrop={(props) => <Overlay {...props} blur={blur} />} onHide={onHide} {...rest} {...generateTestId()}>
      {backdrop ? <SheetBody {...bodyProps}>{children}</SheetBody> : <NoBackdrop {...bodyProps}>{children}</NoBackdrop>}
    </SheetContainer>
  );
};
